import { useCallback, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { EyesIcon, XIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { allPlacesHintDismissedAtom } from "@/store/atoms";

type Props = {
  /** Lets the map's other top overlays sit below this; the copy wraps per breakpoint. */
  onHeightChange?: (height: number) => void;
};

/** One-time hint over the search map, nudging "Places with data" users to "All places". */
export function AllPlacesHint({ onHeightChange }: Props) {
  const i18n = useI18N();
  const [, setDismissed] = useAtom(allPlacesHintDismissedAtom);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !onHeightChange) return;
    const report = () => onHeightChange(el.offsetHeight);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => {
      observer.disconnect();
      onHeightChange(0);
    };
  }, [onHeightChange]);

  const handleDismiss = useCallback(() => setDismissed(true), [setDismissed]);

  return (
    <div
      ref={containerRef}
      className="absolute left-0 right-0 top-0 z-30 bg-container-brand-minimal border-b border-border-token-neutral-minimal"
      role="status"
    >
      <div className="flex items-start gap-3 px-4 py-3 pr-12">
        <EyesIcon
          size={24}
          weight="fill"
          className="shrink-0 text-fg-brand-subtle"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="label-m-semibold text-fg-neutral-strong">
            {i18n.get("allPlacesHintTitle")}
          </p>
          <p className="label-m-medium text-fg-neutral-subtle">
            {i18n.get("allPlacesHintMessage")}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={handleDismiss}
        aria-label={i18n.get("dismissAllPlacesHint")}
        className="absolute right-3 top-3 rounded p-1 text-fg-neutral-subtle hover:text-fg-neutral-strong focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none transition-colors"
      >
        <XIcon size={20} aria-hidden="true" />
      </button>
    </div>
  );
}

export default AllPlacesHint;
